import { bindable, autoinject, bindingMode } from 'aurelia-framework';
import { DialogService } from 'aurelia-dialog';
import { GetToken } from '../register-organization/get-token';
import { RegisterCustomer } from '../register-organization/register-customer';
import { CustomerInfoStore, RegisterCustomerInputModel, CustomerRegistrationInfo, RegisterAction } from '../../services/customer-info-store';
import { OrganizationInfo, FeatureInfo, OrganizationProperties } from '../../services/organization-info';
import { HttpClient, json } from 'aurelia-fetch-client';
import { RequestLicenseResponse } from '../../typings/RequestLicense';
import { formatLog } from '../../utils/formatter';


@autoinject
export class LicensingOverview {

    get company(): string {
        return this.customer && this.customer.company || null;
    }

    organizationId: string = null;
    organizationName: string = null;
    usefmnl: boolean = false;
    customer: CustomerRegistrationInfo = null;
    validating: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.twoWay }) invalidLicenses: FeatureInfo[] = [];

    constructor(private dialogService: DialogService, private customerInfoStore: CustomerInfoStore, private orgInfo: OrganizationInfo, private http: HttpClient) {
        http.configure(x => {
            x.useStandardConfiguration()
        });
    }

    activate(routeParams: OrganizationProperties) {
        console.log(formatLog('Activate licensing-overview'), routeParams);

        this.organizationId = routeParams.organizationId;
        this.organizationName = routeParams.organizationName;
        this.usefmnl = routeParams.usefmnl.toString() === "true";
        this.customer = this.customerInfoStore.getCustomerInfo(routeParams.organizationId, null);

        console.log('usefmnl', this.usefmnl);

        const prefix = this.getPrefix();

        // Validate two alternate keys
        this.validateAlternateKey(`${prefix}_license`, `${prefix}_featurecodekey`);
        this.validateAlternateKey(`${prefix}_keyvalue`, `${prefix}_key`);

        // Perform an environment check
        this.checkEnvironment();
    }

    async validateAlternateKey(entityName: string, keyName: string): Promise<any> {
        console.log(formatLog("Validating alternate key."), entityName, keyName);
        try {
            const alternateKeyResponse = await this.orgInfo.retrieveAlternateKeyStatus(entityName, keyName);

            // When response contains an error that may mean that the entity does not exist
            if (this.orgInfo.isErrorResponse(alternateKeyResponse)) {

                console.log(formatLog("Error while validating alternate key."), alternateKeyResponse);
            }
            else if (alternateKeyResponse.EntityKeyIndexStatus !== "Active") {

                const prefix = this.getPrefix();

                this.orgInfo.tryRepairAlternateKey(entityName, prefix, keyName, alternateKeyResponse);
            }
        } 
        catch (e) {
            console.error(formatLog("Alternate key validation failed: "), e);
        }
    }

    getToken() {

        const input = { "Id": this.organizationId, "UniqueName": this.organizationName };

        try {

            this.dialogService.open({ viewModel: GetToken, model: input, lock: true }).whenClosed(response => {
                if (!response.wasCancelled) {
                    console.log(formatLog('Good response from dialog - '), response);
                } 
                else {
                    console.log(formatLog('Dialog canceled'));
                }
            });

        }
        catch (e) {
            console.log(formatLog('Error opening dialogservice'), e);

            alert('Unable to open dialog for new organization, please try a different browser, such as Chrome or Mozilla Firefox.');
        }
    }

    updateRegistration() {
        try {

            let isProduction = false;
            // Find for current organization if it's a production instance
            this.customer.organizations.forEach(org => {
                if (org.id === this.organizationId) {
                    isProduction = org.isProduction;
                }
            });

            const input: RegisterCustomerInputModel = {
                "id": this.customer.id,
                "company": this.customer.company,
                "email": this.customer.email,
                "gdprConsentGiven": this.customer.gdprConsentGiven,
                "isProduction": isProduction,
                "name": this.customer.name,
                "organizationId": this.organizationId,
                "organizationName": this.organizationName,
                "signedUpForNewsLetter": this.customer.signedUpForNewsLetter,
                "registerAction": RegisterAction.Update
            };

            this.dialogService.open({ viewModel: RegisterCustomer, model: input, lock: true }).whenClosed(response => {
                if (!response.wasCancelled) {
                    console.log(formatLog('Good response from dialog - '), response);
                } else {
                    console.log(formatLog('Dialog canceled'));
                }
            });
        }
        catch (e) {
            console.log(formatLog('Error opening dialogservice'), e);

            alert('Unable to open dialog for new organization, please try a different browser.');
        }
    }

    async checkInvalidLicenses() {
        this.validating = true;

        // Check each invalid license
        const tasks = this.invalidLicenses.map(async (solution) => {
            solution.validating = true;

            const contractLines = this.customer.contractLines || [];
            const applicableContractLine = contractLines.find(cl => cl.features.find(f => f.id === solution.featureCode) != null) || null;

            if (applicableContractLine !== null) {
                if (applicableContractLine.usageQuery) {
                    const usage = await this.orgInfo.retrieveUsage(applicableContractLine.usageQuery);
                    solution.usage = usage;
                }
            }

            await this.requestLicense(solution);

            // If valid, remove item from invalidLicenses list
            if (solution.licenseStatus === "Valid") {
                var idx = this.invalidLicenses.indexOf(solution);
                this.invalidLicenses.splice(idx, 1);
                // Reset some properties
                solution.validating = false;
                solution.remainingDays = null;
            }
        });

        await Promise.all(tasks);

        this.validating = false;
    }

    async checkEnvironment() {

        console.log(formatLog("Check environment."));
        const environmentUrl = await this.customerInfoStore.getEnvironmentUrl();
        console.log(formatLog("Environment URL: "), environmentUrl);

        const request = {

            "id": this.organizationId,
            "environmentUrl": environmentUrl
        };

        await this.http
            .fetch("/api/environment/check", {
                method: 'post',
                body: json(request)
            })
            .catch(async e => { throw new Error(await e.text()); })
    }

    async requestLicense(feature: FeatureInfo) {

        const request = {
            "organizationId": this.organizationId,
            "featureCode": feature.featureCode,
            "usage": feature.usage
        };

        await this.http
            .fetch("/api/requestlicense", {
                method: 'post',
                body: json(request)
            })
            .then(response => response.json())
            .catch(async e => { throw new Error(await e.text()); })
            .then(async (licenseResponse: RequestLicenseResponse) => {
                
                console.log(formatLog("Handle the Request License response"), licenseResponse);
                
                await this.handleRequestLicenseResponse(feature, licenseResponse);
            })

            .catch((error) => {
                // Something wrong, show message in licenseStatus field
                console.log(formatLog('Error validating license: '), error);
                feature.licenseStatus = error;
                feature.validating = false;
            });
    }

    private getPrefix() {
        if (this.usefmnl) {
            return "fmnl";
        }
        return "cnm";
    }

    private async handleRequestLicenseResponse(feature: FeatureInfo, licenseResponse: RequestLicenseResponse) {

        // Set status
        feature.licenseStatus = licenseResponse.licenseStatus;

        if (!licenseResponse.isConsumptionBased) {
            //quick fix to remove date component
            licenseResponse.expirationDate = licenseResponse.expirationDate.split("T")[0];
            licenseResponse.finalExpirationDate = licenseResponse.finalExpirationDate.split("T")[0];

            if (licenseResponse.licenseStatus === "Trial") {
                this.orgInfo.determineRemainingDaysForLicense(feature, licenseResponse.expirationDate);
            }
        }

        try {

            const prefix = this.getPrefix();

            if (licenseResponse.isConsumptionBased) {
                await this.orgInfo.storeConsumptionBasedLicense(feature.featureName, licenseResponse, prefix);
            }
            else {
                await this.orgInfo.storeLicense(feature.featureName, licenseResponse, prefix);
            }
        } 
        catch (e) {
            console.log(formatLog("Error while storing license"), e);
            feature.licenseStatus = "Error while saving license: " + e;
        } 
        finally {
            feature.validating = false;
        }
    }
}



// WEBPACK FOOTER //
// ./ClientApp/app/components/licensing-overview/licensing-overview.ts